/**
 * Right-rail cart. Flat summary — item count beside the pre-tax subtotal,
 * a tax row, and the tax-inclusive total — so the arithmetic (cost + tax
 * = total) is explicit and no single amount is ambiguous. Tax uses the
 * same pre-submit rate/rounding as the checkout summary, so the rail's
 * total equals what checkout shows. Empty state mirrors the design.
 *
 * The cart spans EVERY campus the admin manages (the store buckets lines
 * per campus, and checkout reviews them all), so the rail totals the whole
 * cart and — when more than one campus holds lines — lists each campus's
 * count. A rail scoped to the selected campus reads "empty" right after a
 * sibling campus's items were added or one campus's order was placed,
 * which is how remaining items got lost (c20d4f27 follow-up).
 */

import { Button } from '@ordo/ui/button'
import { cn } from '@ordo/ui/cn'
import type { ReactElement } from 'react'
import { useNavigate } from 'react-router'

import type { AdminSchool } from '@/api/school-orders'
import { EstimatedTaxRow } from '@/components/orders/school/estimated-tax-row'
import { crossCampusCartSummary } from '@/lib/school-orders/checkout-scope'
import { formatCents } from '@/lib/school-orders/money'
import { useSchoolCart } from '@/stores/school-cart'

export function CartPanel({
  school,
  schools,
  className,
}: {
  school: AdminSchool
  /** All the admin's campuses; the rail falls back to the selected one alone. */
  schools?: AdminSchool[]
  className?: string
}): ReactElement {
  const navigate = useNavigate()
  const linesByLocation = useSchoolCart((s) => s.linesByLocation)
  const scope = schools && schools.length > 0 ? schools : [school]
  const summary = crossCampusCartSummary(scope.map((s) => ({ school: s, lines: linesByLocation[s.id] ?? [] })))

  return (
    <div className={cn('flex flex-col rounded-xl border bg-card p-4', className)}>
      <h2 className="shrink-0 text-base font-semibold text-foreground">Cart</h2>
      {summary.empty ? (
        <div className="mt-3 grid place-items-center rounded-lg bg-muted/50 py-6 text-sm text-muted-foreground">
          Your cart is empty
        </div>
      ) : (
        <>
          {summary.campuses.length > 1 ? (
            // Per-campus counts so items on a sibling campus are visible
            // from any campus's ordering page.
            <ul className="mt-3 flex shrink-0 flex-col gap-1 border-b pb-2 text-sm">
              {summary.campuses.map((campus) => (
                <li key={campus.school.id} className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-muted-foreground">{campus.school.name}</span>
                  <span className="shrink-0 tabular-nums text-foreground">
                    {campus.qty} {campus.qty === 1 ? 'item' : 'items'}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          <dl className="mt-3 flex shrink-0 flex-col gap-1.5 text-sm">
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-muted-foreground">
                {summary.qty} {summary.qty === 1 ? 'item' : 'items'}
              </dt>
              <dd className="tabular-nums text-foreground">
                {summary.subtotalCents !== null ? formatCents(summary.subtotalCents, summary.currency) : '—'}
              </dd>
            </div>
            {/* Always present, and now honest about WHAT it is: school bulk
                orders are billed campus-rate tax at invoicing, so the row
                estimates that rather than showing a $0.00 the invoice will
                contradict. */}
            <EstimatedTaxRow estimatedTaxCents={summary.estimatedTaxCents} currency={summary.currency} />
            <div className="mt-0.5 flex items-baseline justify-between gap-2 border-t pt-2">
              <dt className="font-medium text-foreground">Total</dt>
              <dd className="font-semibold tabular-nums text-foreground">
                {summary.totalCents !== null ? formatCents(summary.totalCents, summary.currency) : '—'}
              </dd>
            </div>
          </dl>
          <Button className="mt-4 w-full shrink-0" onClick={() => navigate('/orders/school/checkout')}>
            Review &amp; Checkout
          </Button>
        </>
      )}
    </div>
  )
}
